import { Header, SpaceBetween, Icon, BreadcrumbGroup } from "@cloudscape-design/components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PRODUCT_SCHEMAS } from "./productSchemas";
import "./CreateProductLandingPage.css";
import { useDocumentTitle } from "./useDocumentTitle";

const ICONS: Record<string, "globe" | "lock-private" | "script" | "grid-view" | "upload-download" | "group-active"> = {
  "web-api-be": "globe",
  "web-api-fe": "globe",
  "access-secret": "lock-private",
  "generic-lambda": "script",
  "lambda-container": "grid-view",
  "sqs-queue": "upload-download",
  "fm-bundle": "grid-view",
  "custom": "group-active",
};

const ACCENTS: Record<string, string> = {
  "web-api-be": "indigo",
  "web-api-fe": "teal",
  "access-secret": "amber",
  "generic-lambda": "slate",
  "lambda-container": "indigo",
  "sqs-queue": "teal",
  "fm-bundle": "amber",
  "custom": "slate",
};

/**
 * Picks a product type to configure. Which project the product belongs to is chosen inside the
 * wizard itself (see CreateProductPage's Project field), not here — that way the project
 * selector only ever lists projects that already exist, so a product can never be created ahead
 * of/without its project.
 */
export default function CreateProductLandingPage() {
  useDocumentTitle("Create Product");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Forward the project context (set when arriving from a project's "Create Product" button) to
  // the selected tile's wizard, so the project comes pre-selected there.
  const projectId = searchParams.get("projectId");
  const suffix = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: "Projects", href: "/projects" },
          { text: "Create Product", href: "#" },
        ]}
        onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
      />
      <Header variant="h1" description="Choose a product type to configure and submit a new deployment request.">
        Create Product
      </Header>
      <div className="product-picker-grid">
        {PRODUCT_SCHEMAS.map((schema) => (
          <button
            key={schema.key}
            type="button"
            className={`product-tile accent-${ACCENTS[schema.key] ?? "indigo"}`}
            onClick={() => navigate(`/create/${schema.key}${suffix}`)}
          >
            <div className="product-tile-icon">
              <Icon name={ICONS[schema.key] ?? "grid-view"} size="big" variant="inverted" />
            </div>
            <div className="product-tile-title">{schema.title}</div>
            <div className="product-tile-description">{schema.description}</div>
            {!schema.available && <div className="product-tile-badge">Coming soon</div>}
          </button>
        ))}
      </div>
    </SpaceBetween>
  );
}
